import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Stack,
} from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import StyledLoadingButton from "../../../../../common/providers/theme/design-tokens/LoadingButton/StyledLoadingButton";
import {
  MITIGATED_RISKS,
  MITIGATED_RISKS_ENUM,
} from "../common/events.constants";
import { IRecentEventsTableRow } from "../common/events.models";

interface IFeedbackForm {
  risks: Record<string, boolean>;
}

interface IFeedbackFormProps {
  submitLoading?: boolean;
  rowData: IRecentEventsTableRow;
  isDisabled: boolean;
  onSubmitClick: (
    params: IRecentEventsTableRow,
    mitigatedRisk: string[]
  ) => void;
}

const FeedbackDialogForm = ({
  rowData,
  isDisabled,
  onSubmitClick,
  submitLoading,
}: IFeedbackFormProps): JSX.Element => {
  const filteredMitigatedRisks = MITIGATED_RISKS.filter(
    risk => risk !== MITIGATED_RISKS_ENUM.LOSS_OF_CONTAIMENT_PERCENT
  );

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<IFeedbackForm>({
    defaultValues: {
      risks: filteredMitigatedRisks.reduce((acc, risk) => {
        const mitigatedRiskFound = rowData.mitigatedRisk?.includes(risk);

        acc[risk] = Boolean(mitigatedRiskFound);

        return acc;
      }, {} as Record<string, boolean>),
    },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IFeedbackForm> = data => {
    const selectedItems = Object.keys(data.risks).filter(
      key => data.risks[key]
    );

    onSubmitClick(rowData, selectedItems);
  };

  const selectedRisks = watch("risks");

  const isAtLeastOneSelected = Object.values(selectedRisks).some(Boolean);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup>
        {filteredMitigatedRisks.map(risk => (
          <Controller
            key={risk}
            name={`risks.${risk}`}
            control={control}
            disabled={isDisabled}
            rules={{
              validate: () =>
                isAtLeastOneSelected || "Please select at least one option.",
            }}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={risk}
              />
            )}
          />
        ))}
      </FormGroup>
      {errors.risks && !isAtLeastOneSelected && (
        <FormHelperText error sx={{ fontSize: "0.75em" }}>
          Please select at least one option.
        </FormHelperText>
      )}
      <Stack
        display="flex"
        flexDirection="row"
        justifyContent="center"
        gap={2}
        sx={{ paddingTop: 3 }}
      >
        <StyledLoadingButton
          type="submit"
          loading={submitLoading}
          disabled={true}
          startIcon={null}
          loadingPosition="start"
          variant="contained"
          color="primary"
        >
          Save feedback
        </StyledLoadingButton>
      </Stack>
    </form>
  );
};

export default FeedbackDialogForm;
